import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import EmailPrefsToast from "@/components/EmailPrefsToast";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ErrorBoundary, GTMPageView } from "@hoalaw/ui";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { PointsProvider } from "@/contexts/PointsContext";
import Layout from "@/components/layout/Layout";
import { SeoHead } from "@/components/seo/SeoHead";
import { CookieConsentBanner } from "@/components/ui/CookieConsentBanner";
import { ThirdPartyScripts } from "@/components/analytics/ThirdPartyScripts";
import { recordSiteVisit } from "@/lib/analytics";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { RedirectKeepingLocation } from "@/components/routing/RedirectKeepingLocation";
import { productConfig } from "@/config/product";
import { features } from "@/config/features";

// Eager: the home page is prerendered into index.html, and a lazy chunk would
// flash the Suspense fallback over that markup on first render.
import Home from "@/pages/Home";

const Chatbot = lazy(() => import("@/pages/Chatbot"));
const LawChanges = lazy(() => import("@/pages/LawChanges"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Login = lazy(() => import("@/pages/Login"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const EducationRedeem = lazy(() => import("@/pages/EducationRedeem"));
const Signup = lazy(() => import("@/pages/Signup"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const AdminLanding = lazy(() => import("@/pages/AdminLanding"));
const AdminCRM = lazy(() => import("@/pages/AdminCRM"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminEducationPartners = lazy(() => import("@/pages/AdminEducationPartners"));
const AdminSystemHealth = lazy(() => import("@/pages/AdminSystemHealth"));
const AdminEmail = lazy(() => import("@/pages/AdminEmail"));
const AdminMarketingIntelligence = lazy(() => import("@/pages/AdminMarketingIntelligence"));
const AdminMessageEvaluation = lazy(() => import("@/pages/AdminMessageEvaluation"));
const AdminUserFeedback = lazy(() => import("@/pages/AdminUserFeedback"));
const AdminUserDetails = lazy(() => import("@/pages/AdminUserDetails"));
const AppShell = lazy(() => import("@/components/layout/AppShell"));
const AuthenticatedAppProviders = lazy(() => import("@/components/app/AuthenticatedAppProviders"));
const DocumentLibraryPage = lazy(() => import("@/pages/DocumentLibraryPage"));
const GoverningDocumentsPage = lazy(() => import("@/pages/GoverningDocumentsPage"));
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const ReviewStart = lazy(() => import("@/pages/ReviewStart"));
const ReviewClaim = lazy(() => import("@/pages/ReviewClaim"));
const GenerateStart = lazy(() => import("@/pages/GenerateStart"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));

function RedirectWithSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

const queryClient = new QueryClient();

const PointsFeatureProvider = ({ children }: { children: ReactNode }) => (
  productConfig.admin.pointsEnabled ? <PointsProvider>{children}</PointsProvider> : <>{children}</>
);

/** Records this tab's site visit once ("How they found us"). An Astro page
 *  opened earlier in the same tab has already recorded it; then this is a no-op. */
function SiteVisitRecorder() {
  useEffect(() => {
    recordSiteVisit();
  }, []);
  return null;
}

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <SubscriptionProvider>
        <PointsFeatureProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              {/* Keep Helmet outside Suspense so abandoned lazy-route renders
                  cannot retain metadata from the previous URL. */}
              <SeoHead />
              <ThirdPartyScripts />
              <SiteVisitRecorder />
              <GTMPageView gtmId={import.meta.env.VITE_GTM_ID} />
              <EmailPrefsToast />
              <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
                  {/* Pure redirects sit outside both layouts — they render
                      nothing, so wrapping them would only flash a navbar or a
                      sidebar on the way through. */}
                  <Route path="/chatbot" element={<RedirectWithSearch to="/chat" />} />
                  <Route path="/profile" element={<Navigate to="/account" replace />} />
                  {productConfig.admin.crmEnabled && (
                    <Route path="/admin/crm" element={<RedirectKeepingLocation to="/admin/users" />} />
                  )}

                  {/* Public pages keep the marketing Layout: @hoalaw/ui navbar
                      and footer. */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/education/redeem" element={<EducationRedeem />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/disclaimer" element={<Disclaimer />} />
                    {/* Always registered, never gated at the <Route> level: this is a
                        literal static path that must outrank the dynamic /review/:id
                        below whatever the flags say. Gating it meant React Router saw
                        no match and fell through to /review/:id with id="start", which
                        is behind ProtectedRoute — bouncing an anonymous visitor to
                        /login instead of showing a graceful unavailable state.
                        ReviewStart checks features.reviewPreview itself. */}
                    <Route path="/review/start" element={<ReviewStart />} />
                    {features.communications && (
                      <>
                        <Route path="/generate/start" element={<GenerateStart />} />
                        <Route path="/tools" element={<ToolsPage />} />
                      </>
                    )}
                    <Route path="*" element={<NotFound />} />
                  </Route>

                  {/* Everything signed-in renders inside the sidebar shell. */}
                  <Route
                    element={
                      <AuthenticatedAppProviders>
                        <AppShell />
                      </AuthenticatedAppProviders>
                    }
                  >
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                      <Route path="/law-changes" element={<ProtectedRoute><LawChanges /></ProtectedRoute>} />
                    <Route path="/chat" element={
                      <ProtectedRoute>
                        <Chatbot />
                      </ProtectedRoute>
                    } />
                    {features.review && (
                      <>
                        {/* Same collision risk as /review/start — registered whenever
                            /review is, regardless of features.reviewPreview, so this
                            literal path always beats /review/:id. ReviewClaim checks
                            the flag itself. */}
                        <Route path="/review/claim" element={
                          <ProtectedRoute>
                            <ReviewClaim />
                          </ProtectedRoute>
                        } />
                        <Route path="/review" element={
                          <ProtectedRoute>
                            <ReviewPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/review/:id" element={
                          <ProtectedRoute>
                            <ReviewPage />
                          </ProtectedRoute>
                        } />
                      </>
                    )}
                    {features.documentLibrary && (
                      <Route path="/library" element={
                        <ProtectedRoute>
                          <DocumentLibraryPage />
                        </ProtectedRoute>
                      } />
                    )}
                    {features.governingDocuments && (
                      <Route path="/documents" element={
                        <ProtectedRoute>
                          <GoverningDocumentsPage />
                        </ProtectedRoute>
                      } />
                    )}
                    <Route path="/account" element={
                      <ProtectedRoute>
                        <UserProfile />
                      </ProtectedRoute>
                    } />
                    {productConfig.admin.crmEnabled && (
                      <>
                        <Route path="/admin" element={
                          <ProtectedRoute requiresAdmin>
                            <AdminLanding />
                          </ProtectedRoute>
                        } />
                        <Route path="/admin/users" element={
                          <ProtectedRoute requiresAdmin>
                            <AdminCRM />
                          </ProtectedRoute>
                        } />
                        <Route path="/admin/analytics" element={
                          <ProtectedRoute requiresAdmin>
                            <AdminAnalytics />
                          </ProtectedRoute>
                        } />
                        <Route path="/admin/education-partners" element={
                          <ProtectedRoute requiresAdmin>
                            <AdminEducationPartners />
                          </ProtectedRoute>
                        } />
                        {productConfig.admin.evaluationsEnabled && (
                          <Route path="/admin/message-evaluation" element={
                            <ProtectedRoute requiresAdmin>
                              <AdminMessageEvaluation />
                            </ProtectedRoute>
                          } />
                        )}
                        {productConfig.admin.userFeedbackEnabled && (
                          <Route path="/admin/user-feedback" element={
                            <ProtectedRoute requiresAdmin>
                              <AdminUserFeedback />
                            </ProtectedRoute>
                          } />
                        )}
                        <Route path="/admin/users/:userId" element={
                          <ProtectedRoute requiresAdmin>
                            <AdminUserDetails />
                          </ProtectedRoute>
                        } />
                        {productConfig.admin.systemHealthEnabled && (
                          <Route path="/admin/system-health" element={
                            <ProtectedRoute requiresAdmin>
                              <AdminSystemHealth />
                            </ProtectedRoute>
                          } />
                        )}
                        {productConfig.admin.emailAnalyticsEnabled && (
                          <Route path="/admin/email" element={
                            <ProtectedRoute requiresAdmin>
                              <AdminEmail />
                            </ProtectedRoute>
                          } />
                        )}
                        {productConfig.admin.marketingIntelligenceEnabled && (
                          <Route path="/admin/marketing-intelligence" element={
                            <ProtectedRoute requiresAdmin>
                              <AdminMarketingIntelligence />
                            </ProtectedRoute>
                          } />
                        )}
                      </>
                    )}
                  </Route>
              </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </PointsFeatureProvider>
      </SubscriptionProvider>
    </AuthProvider>
    {productConfig.privacy.cookieConsentEnabled && <CookieConsentBanner />}
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
